import { TFunction } from "react-i18next";
import { ListConfigFilter } from "go-list/core/components/Filter/services/types";
import { MenuPriceListFlatApi } from "../../../../../../../../../../../../../services/Api/Organization/types";
import { VirtualTableColumn } from "../../../../../../../components/VirtualTable/types";

type Accumulator = {
	setIds: Set<number>;
	setNames: Set<string>;
	entries: [number, string][];
};

export const getPossibleSpreadsheetPriceListDefaultColumns = (t: TFunction<"translation", undefined>) => [
	{
		id: "category_name",
		name: t("common.word.category"),
	},
	{
		id: "item_group_name",
		name: t("common.word.item_group"),
	},
	{
		id: "item_name",
		name: t("common.word.variant"),
	},
	{
		id: "item_full_name",
		name: t("common.word.item_full_name"),
	},
	{
		id: "modifier_group_name",
		name: t("modules.group_edit.field.group.title"),
	},
	{
		id: "sub_item_name",
		name: t("modules.group_edit.field.sub_item.title"),
	},
	{
		id: "price",
		name: t("modules.group_edit.field.default_price.title"),
	},
];

export const getInitialSpreadsheetPriceListDefaultColumns = (t: TFunction): VirtualTableColumn[] => [
	{
		id: "category_name",
		name: t("common.word.category"),
	},
	{
		id: "item_group_name",
		name: t("common.word.item_group"),
	},
	{
		id: "item_name",
		name: t("common.word.variant"),
	},
	{
		id: "modifier_group_name",
		name: t("modules.group_edit.field.group.title"),
	},
	{
		id: "sub_item_name",
		name: t("modules.group_edit.field.sub_item.title"),
	},
	{
		id: "price",
		name: t("modules.group_edit.field.default_price.title"),
	},
];

const getParsedUniqueItemsToFilterOptions = (
	items: MenuPriceListFlatApi[],
	idField: keyof MenuPriceListFlatApi,
	nameField: keyof MenuPriceListFlatApi
) => {
	return Object.fromEntries(
		items.reduce<Accumulator>(
			(acc, item) => {
				const itemId = Number(item[idField]);
				const itemName = item[nameField];

				if (
					typeof itemName === "string" &&
					!isNaN(itemId) &&
					!acc.setIds.has(itemId) &&
					!acc.setNames.has(itemName)
				) {
					acc.setIds.add(itemId);
					acc.setNames.add(itemName);
					acc.entries.push([itemId, itemName]);
				}

				return acc;
			},
			{
				setIds: new Set(),
				setNames: new Set(),
				entries: [],
			}
		).entries
	);
};

export const getSpreadSheetPriceListFilters = (items: MenuPriceListFlatApi[], t: TFunction): ListConfigFilter[] => {
	return [
		{
			id: "category",
			name: t("common.word.item_group_category"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(items, "category_id", "category_name"),
			hasDefaultOptions: false,
			sortAlphabetically: true,
		},
		{
			id: "item",
			name: t("modules.group_edit.field.item.title"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(
				items.filter((item) => item.item_name !== t("common.word.default")),
				"item_id",
				"item_name"
			),
			sortAlphabetically: true,
		},
		{
			id: "item_full",
			name: t("common.word.item_full_name"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(items, "item_id", "item_full_name"),
			sortAlphabetically: true,
		},
		{
			id: "item_group",
			name: t("common.word.item_group"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(items, "item_group_id", "item_group_name"),
			hasDefaultOptions: false,
			sortAlphabetically: true,
		},
		{
			id: "modifier_group",
			name: t("modules.group_edit.field.group.title"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(items, "modifier_group_id", "modifier_group_name"),
			sortAlphabetically: true,
		},
		{
			id: "sub_item",
			name: t("modules.group_edit.field.sub_item.title"),
			type: "list",
			options: getParsedUniqueItemsToFilterOptions(items, "sub_item_id", "sub_item_name"),
			sortAlphabetically: true,
		},
		{
			id: "price",
			name: t("modules.group_edit.field.price.title"),
			type: "number",
			typeOptions: ["a", "u"],
		},
	];
};
